import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Layout from "../components/Layout";
import LiveEmptyState from "../components/LiveEmptyState";
import useLiveMode from "../hooks/useLiveMode";
import api from "../lib/api";
import { formatPct, formatDate } from "../utils/format";
import { Globe, ExternalLink } from "lucide-react";

export default function LandingPages() {
  const [lps, setLps] = useState([]);
  const { isLive, isConnected, hasLiveData } = useLiveMode();
  useEffect(() => {
    api.get("/landing-pages").then((r) => setLps(r.data || [])).catch(() => setLps([]));
  }, []);

  const healthTone = (s) => s >= 80 ? "success" : s >= 60 ? "warning" : "critical";
  // Empty-state policy:
  //   no LIVE  → render data normally (mock seed)
  //   LIVE + connector NOT connected → show connector CTA
  //   LIVE + connector connected → show module-specific empty state ("No landing pages detected yet")
  const noRows = lps.length === 0;
  const showConnectorCta = isLive && noRows && !isConnected;
  const showModuleEmpty = isLive && noRows && isConnected;

  return (
    <Layout title="Landing Page Intelligence" subtitle={`${lps.length} pages`}>
      {showConnectorCta ? (
        <LiveEmptyState entity="landing pages" />
      ) : showModuleEmpty ? (
        <LiveEmptyState
          entity="landing pages"
          variant="module"
          title="No landing pages detected yet"
          message="Your data source is connected. Landing-page intelligence is derived automatically once campaigns drive traffic to a tracked URL — typically within the next sync cycle."
        />
      ) : (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="landing-pages-grid">
        {lps.map((lp) => (
          <Link
            key={lp.id}
            to={`/landing-pages/${lp.id}`}
            data-testid={`lp-card-${lp.id}`}
            className={`bg-card border border-border rounded-sm p-5 kpi-border-${healthTone(lp.health_score)} hover:border-primary/50 hover:shadow-sm transition-all block`}
          >
            <div className="flex items-start justify-between">
              <div className="flex items-start gap-2 flex-1 min-w-0">
                <Globe className="w-4 h-4 text-muted-foreground mt-0.5" strokeWidth={1.5} />
                <div className="flex-1 min-w-0">
                  <div className="font-heading font-medium text-sm mb-0.5">{lp.title}</div>
                  <div className="text-xs text-primary truncate flex items-center gap-1">
                    {lp.url} <ExternalLink className="w-3 h-3" />
                  </div>
                </div>
              </div>
              <div className="text-right">
                <div className="label-mono">Health</div>
                <div className="font-heading text-2xl font-light tabular">{lp.health_score}</div>
              </div>
            </div>

            <div className="mt-3 text-xs text-muted-foreground">Client: {lp.client_name}</div>

            <div className="grid grid-cols-3 gap-2 mt-4 pt-4 border-t border-border text-sm">
              <div><div className="label-mono">Clicks · 7d</div><div className="font-semibold tabular mt-0.5">{lp.clicks_7d}</div></div>
              <div><div className="label-mono">Leads · 7d</div><div className="font-semibold tabular mt-0.5">{lp.leads_7d}</div></div>
              <div><div className="label-mono">Conv Rate</div><div className="font-semibold tabular mt-0.5">{formatPct(lp.conversion_rate, 2)}</div></div>
            </div>

            <div className="mt-3 text-[11px] text-muted-foreground">Updated {formatDate(lp.last_updated)} · Linked to {lp.linked_campaigns?.length || 0} campaigns</div>
            <div className="mt-2 text-xs text-primary font-medium">Open detail →</div>
          </Link>
        ))}
      </div>
      )}
    </Layout>
  );
}
